import { safeCode } from './naming';

export type MachineStatus = 'ON' | 'OFF';
export type ProposalStatus =
  | 'proposed'
  | 'approved'
  | 'rejected'
  | 'deferred'
  | 'withdrawn'
  | 'executing'
  | 'executed'
  | 'failed'
  | string;
export type RecommendationTier = 'auto_recommend' | 'needs_review' | string;
export type ApprovalDecision = 'approved' | 'rejected' | 'deferred';
export type ConfidenceTier = 'measured' | 'estimated' | 'forecast-low' | string;
export type StageTone = 'capture' | 'coverage' | 'restore' | 'protect' | 'config' | 'neutral';

const STAGE_FRAGMENTS: Record<string, { label: string; tone: StageTone }> = {
  'target group gap': { label: 'Target Group Gap', tone: 'capture' },
  'exact keyword campaign gap': { label: 'Exact Keyword Campaign Gap', tone: 'coverage' },
  'reboot ads': { label: 'Reboot Ads', tone: 'restore' },
  'negative contribution profit': { label: 'Negative Contribution Profit', tone: 'protect' },
  'objective version is outdated': { label: 'Objective Version is OUTDATED', tone: 'config' },
};

export type MachineProposal = {
  proposal_id: string;
  domino: string;
  action_type: string;
  keyword: string | null;
  market: string | null;
  asin: string | null;
  product: string | null;
  variant: string | null;
  target_group: string | null;
  target_group_provenance?: 'assigned' | 'suggested' | null;
  search_volume: number | null;
  recommended_action: string;
  recommended_target: string | null;
  recommendation_tier: RecommendationTier;
  units_effect: string | null;
  profit_effect: number | null;
  reason: string | null;
  confidence: number | null;
  confidence_tier: ConfidenceTier | null;
  status: ProposalStatus;
  updated_at: string | null;
  operator_note: string | null;
  note_updated_by: string | null;
  note_updated_at: string | null;
};

export type IntakeFreshness = {
  leg: string;
  last_at: string | null;
  stale: boolean;
};

export type CampaignTemplate = {
  name: string;
  campaign_type: string;
  funnel_stage: string;
  match_type: string;
  placement: string;
  placement_percentage: number;
  bidding_strategy: string;
  default_budget: number;
  si_objective: string;
  retired?: boolean;
  description?: string;
};

export type CampaignTemplateMeta = {
  source?: string;
  updated_at?: string | null;
  updated_by?: string | null;
};

export type MachineConfig = {
  kill_state: MachineStatus;
  kill_meta: {
    updated_at: string | null;
    updatedBy: string | null;
    reason: string | null;
  } | null;
  domino_order: string[];
  domino_thresholds: Record<string, unknown>;
  campaign_templates?: string[];
  campaign_template_default?: string | null;
  campaign_template_catalogue?: CampaignTemplate[];
  campaign_template_meta?: CampaignTemplateMeta;
};

export const OBJECTIVE_NULL_BUCKET_LABEL = 'NONE';

export type ObjectiveBand = 'green' | 'amber' | 'red' | 'unknown';

export type ObjectiveSummaryRow = {
  objective: string;
  objective_tag: string | null;
  campaign_type: string | null;
  campaigns_total: number | null;
  markets: string | null;
  spend: number | null;
  sales: number | null;
  clicks: number | null;
  profit: number | null;
  profit_yield: number | null;
  acos: number | null;
  margin_imputed_share: number | null;
  margin_unknown_share: number | null;
  conforming: number | null;
  judged: number | null;
  band: ObjectiveBand;
};

export type ObjectiveDetailRow = {
  objective: string;
  campaign_type: string | null;
  market: string;
  account: string | null;
  campaign_name: string;
  is_live: boolean;
  acos: number | null;
  spend: number | null;
  sales: number | null;
  profit: number | null;
  profit_yield: number | null;
  margin_unknown_share: number | null;
  conformance_status: string | null;
  conformance_reasons: string[];
  band: ObjectiveBand;
};

export type ObjectiveWindow = {
  start: string;
  end: string;
  max_fact_date: string;
  attribution_mature_through: string;
};

export type ObjectivesSection = {
  summary: ObjectiveSummaryRow[];
  detail: ObjectiveDetailRow[];
  window: ObjectiveWindow | null;
};

export type MachineStats = {
  mece_coverage: Record<string, unknown>[];
  tg_summary: Record<string, unknown>[];
};

export type TargetGroupOption = {
  tg_id: string;
  tg_name: string;
  tg_asin: string | null;
  status: string;
};

export type ProductOption = {
  asin: string;
  market: string;
  name: string | null;
};

export type DominoRegistryEntry = {
  id: string;
  label: string;
};

export type DominoOrderEntry = {
  id: string;
  label: string | null;
};

export type MachineExperiment = Record<string, unknown>;
export type TacticExperiment = {
  experiment_id: string;
  application_id: string;
  tactic_id: string;
  treatment: string | null;
  item_key: string;
  keyword: string;
  market: string;
  asin: string | null;
  value_model: string;
  delta_kind: string;
  state: string;
  trial_start: string | null;
  time_under_test: string | number | null;
  time_under_test_unit: 'hours' | null;
  matured: boolean;
  result: 'PASS' | 'FAIL' | 'PENDING';
  confidence: string | number | null;
};
export type TacticIntervention = {
  intervention_type: string;
  params: Record<string, unknown>;
};

export type TacticVersion = {
  version: number;
  is_current: boolean;
  interventions: TacticIntervention[];
  created_by: string | null;
  created_at: string | null;
};

export type TacticDefinition = {
  tactic_id: string;
  name: string;
  description: string | null;
  status: string;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
  version_count: number;
  current_version: number;
  versions: TacticVersion[];
  signal_ids: string[];
};

export type TacticDraft = {
  draft_id: string;
  tactic_id: string;
  tactic_version: number | null;
  signal_id: string;
  item_key: string;
  keyword: string;
  market: string;
  asin: string | null;
  delta_kind: string;
  action_type: string;
  value_model: string;
  expected_delta_value: number | null;
  confidence_tier: string;
  value_basis: string;
  is_spend_increasing: boolean;
  item_value: number | null;
  band_rank: number | null;
  resolved_interventions: TacticIntervention[];
  status: string;
};

export type TacticDraftsQuery = {
  signal_id?: string;
  market?: string;
  tier?: string;
  limit?: number;
  offset?: number;
};

export type TacticDraftsPage = {
  items: TacticDraft[];
  total: number;
  limit: number;
  offset: number;
};
export type ProposalFilter = { domino?: string; market?: string; text?: string; status?: string };
export type ProposalMatchType = 'exact' | 'phrase' | 'broad';
export type ProposalSortKey = 'impact' | 'domino' | 'market' | 'status';
export type SortDirection = 'asc' | 'desc';

export type MachineDecisionState = {
  approvals: MachineProposal[];
  objectives: ObjectivesSection;
  config: MachineConfig | null;
  intake_freshness: IntakeFreshness[] | null;
  stats: MachineStats | null;
  experiments: MachineExperiment[] | null;
  publish_readback: unknown | null;
  error: string | null;
  status: number;
};

export type SectionAvailability = {
  approvals: boolean;
  objectives: boolean;
  config: boolean;
  intake_freshness: boolean;
  stats: boolean;
  experiments: boolean;
  publish_readback: boolean;
};

export type StatusChipTone = 'neutral' | 'ok' | 'warn' | 'bad' | 'purple';

/**
 * The cockpit's sections.
 *
 * These no longer RENDER as a tab row: the cockpit is a docked module of the
 * One OS Portal and the portal's shell owns the tab bar (Damien, 2026-08-26).
 * What survives here is the id/label/href set, which backs `MachineTabId` and
 * is MIRRORED by the portal's manifests/meteoric_machine.json.
 *
 * Keep the two in step. A section added here without a tab in that manifest is
 * a page with no way to reach it.
 */
export const MACHINE_TABS = [
  { id: 'home', label: 'Home', href: '/machine' },
  { id: 'review', label: 'Review queue', href: '/machine/review' },
  { id: 'defer', label: 'Defer-to-human', href: '/machine/defer' },
  { id: 'maintain', label: 'Maintain', href: '/machine/maintain' },
  { id: 'stats', label: 'Stats', href: '/machine/stats' },
  { id: 'stacks', label: 'Keyword Status', href: '/machine/stacks' },
  { id: 'change-log', label: 'Change Log', href: '/machine/change-log' },
  { id: 'objectives', label: 'Objectives', href: '/machine/objectives' },
  { id: 'tactics', label: 'Tactics', href: '/machine/tactics' },
  { id: 'drafts', label: 'Drafts', href: '/machine/drafts' },
  { id: 'experiments', label: 'Experiments', href: '/machine/experiments' },
  { id: 'checks', label: 'Checks', href: '/machine/checks' },
  { id: 'config', label: 'Configuration', href: '/machine/config' },
] as const;

export type MachineTabId = (typeof MACHINE_TABS)[number]['id'];

// There are TWO different server predicates on this data, and they are NOT the same rule:
//
//   1. LANE SCOPE  - reader.py::APPROVALS_SCOPES. Classifies WHAT KIND of decision a
//      proposal needs, and deliberately does NOT filter on status (the cockpit's status
//      filter is a separate axis). Mirrored here by `isReviewScope` below.
//   2. WAITING COUNT - reader.py::_BADGES_SQL. Counts what is still WAITING, so it IS
//      status-coupled: a decided proposal is no longer waiting. Mirrored here by
//      `isReviewProposal` / `isDeferProposal`, which feed `splitQueues` and therefore the
//      tab badges (MachineCockpitLayout.astro) and the "N proposals waiting" block
//      (pages/machine/index.astro).
//
// Keep each pair in lockstep with ITS OWN counterpart. Making the two functions below
// status-agnostic "to match APPROVALS_SCOPES" is a category error: it would count
// approved / rejected / withdrawn / executed rows as waiting and render terminal records
// in the Home queue. `isMaintainProposal` is status-agnostic on BOTH sides
// (COUNTIF(is_maintain)), which is why it is not paired here.
export function isReviewProposal(proposal: Pick<MachineProposal, 'status' | 'recommendation_tier'>): boolean {
  return proposal.status === 'proposed' && proposal.recommendation_tier === 'auto_recommend';
}

export function isDeferProposal(proposal: Pick<MachineProposal, 'status' | 'recommendation_tier'>): boolean {
  return proposal.status === 'deferred' || (proposal.status === 'proposed' && proposal.recommendation_tier === 'needs_review');
}

export function isMaintainProposal(proposal: Pick<MachineProposal, 'action_type' | 'recommended_action'>): boolean {
  return proposal.action_type === 'objective_change' || proposal.recommended_action === 'SET_OBJECTIVE';
}

export function splitQueues(proposals: MachineProposal[]): { review: MachineProposal[]; defer: MachineProposal[]; maintain: MachineProposal[] } {
  const maintain = proposals.filter(isMaintainProposal);
  const rest = proposals.filter((proposal) => !isMaintainProposal(proposal));
  return {
    review: rest.filter(isReviewProposal),
    defer: rest.filter(isDeferProposal),
    maintain,
  };
}

export function isCostAvoidanceExclusion(proposal: Pick<MachineProposal, 'recommended_action'>): boolean {
  return typeof proposal.recommended_action === 'string' && proposal.recommended_action.startsWith('EXCLUDE');
}

export function isTgAssignmentAssign(
  proposal: Pick<MachineProposal, 'action_type' | 'recommended_action'>,
): boolean {
  return proposal.action_type === 'tg_assignment' && !isCostAvoidanceExclusion(proposal);
}

export function isCreateCampaign(proposal: Pick<MachineProposal, 'action_type'>): boolean {
  return proposal.action_type === 'create_campaign';
}

// Exclusion breadth encoded in recommended_action (EXCLUDE_EXACT / EXCLUDE_PHRASE / EXCLUDE_BROAD).
const EXCLUSION_MATCH_TYPE: Record<string, ProposalMatchType> = {
  EXCLUDE_EXACT: 'exact',
  EXCLUDE_PHRASE: 'phrase',
  EXCLUDE_BROAD: 'broad',
};

// The match type an exclusion proposal carries, or null when the recommendation
// does not pin one (e.g. a bare EXCLUDE or a DEFER) and the operator must choose.
export function proposalMatchType(proposal: Pick<MachineProposal, 'recommended_action'>): ProposalMatchType | null {
  const action = (proposal.recommended_action ?? '').toUpperCase();
  return EXCLUSION_MATCH_TYPE[action] ?? null;
}

const READABLE_ACTION_TYPE: Record<string, string> = {
  tg_assignment: 'Assign to target group',
  keyword_negation: 'Add negative keyword',
  create_campaign: 'Create campaign',
  bid_change: 'Adjust bid',
  objective_change: 'Change objective',
};

const READABLE_RECOMMENDED_ACTION: Record<string, string> = {
  SET_OBJECTIVE: 'Set objective',
  RAISE_BID: 'Raise bid',
  LOWER_BID: 'Lower bid',
};

function humanizeToken(token: string): string {
  const cleaned = token.replace(/_/g, ' ').trim().toLowerCase();
  return cleaned ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1) : '';
}

// Human-legible product line for a card: "Pure C8 MCT Oil 1000ml (B09SGMVH2F)".
// Falls back to the bare ASIN when the API resolved no product name, and to null
// when the proposal carries no ASIN at all (e.g. objective changes).
export function describeProposalProduct(
  proposal: Pick<MachineProposal, 'asin' | 'product' | 'variant'>,
): string | null {
  if (!proposal.asin) {
    return null;
  }
  const name = [proposal.product, proposal.variant].filter(Boolean).join(' ');
  // Never render a bare ASIN silently: an unresolved product routes through
  // safeCode so it shows "Unknown (ASIN)" rather than an opaque code (audit §4.3).
  return name ? `${name} (${proposal.asin})` : safeCode(proposal.asin, { show: true });
}

export function describeProposalStage(
  proposal: Pick<MachineProposal, 'domino'>,
): { label: string; tone: StageTone } {
  const domino = proposal.domino?.trim();
  if (!domino) {
    return { label: 'Stage unknown', tone: 'neutral' };
  }

  const fragment = domino.split(' - ').at(-1)?.trim().toLowerCase() ?? '';
  return STAGE_FRAGMENTS[fragment] ?? { label: domino, tone: 'neutral' };
}

// The managed AREA a proposal belongs to ("Ads Managed" / "Profit Managed").
// It is already carried in the domino string as segment 2 -- e.g.
// "1 - Ads Managed - Keyword Gap - Target Group Gap" -- so no API or data work
// is needed to surface it; describeProposalStage simply discarded everything but
// the last segment. Returns null when the domino has no area segment (a bare
// stage such as "Reboot Ads"), so callers render nothing rather than a stray
// chip. (KET-1722 / F9)
export function describeProposalArea(
  proposal: Pick<MachineProposal, 'domino'>,
): string | null {
  const segments = (proposal.domino ?? '')
    .split(' - ')
    .map((segment) => segment.trim())
    .filter(Boolean);
  // Segment 1 is the numeric domino index; segment 2 is the area. Anything
  // shorter carries no area (and the final segment is always the stage, so a
  // 2-segment domino is index + stage, not index + area).
  if (segments.length < 3) {
    return null;
  }
  const area = segments[1];
  return /managed$/i.test(area) ? area : null;
}

// `stageToneClasses` did NOT come across from Meteoric. It returned Tailwind
// colour classes — presentation, in a file of parsers — and the two colours it
// picked were a pale green and a pale amber this portal's brand layer does not
// have (quoted here as words, not codes — the raw-hex lint reads comments too). The stage tone itself is real data and stays as `StageTone`;
// how a tone is DRAWN is now `stageBadgeVariant` in the module's `format.ts`,
// beside every other tone-to-badge decision. See that file for why bands are
// carried as words rather than colours here.

// Monthly keyword search volume as a compact string ("1,234"), or null when the
// machine carried no volume for this proposal (create-campaign proposals often don't).
export function formatSearchVolume(volume: number | null): string | null {
  if (volume === null || !Number.isFinite(volume) || volume < 0) {
    return null;
  }
  return Math.round(volume).toLocaleString('en-US');
}

// Plain-language label for what a proposal actually does, replacing raw codes like
// "EXCLUDE_EXACT · TG_ASSIGNMENT". Exclusions read as "Exclude keyword (exact match)";
// a DEFER recommendation (a routing signal, not a mutation) falls back to the mutation
// the action_type describes.
export function describeProposalAction(
  proposal: Pick<MachineProposal, 'recommended_action' | 'action_type'>,
): string {
  const action = (proposal.recommended_action ?? '').toUpperCase();
  if (action.startsWith('EXCLUDE')) {
    const matchType = proposalMatchType(proposal);
    return matchType ? `Exclude keyword (${matchType} match)` : 'Exclude keyword (match type unset)';
  }
  if (action === '' || action === 'DEFER') {
    return READABLE_ACTION_TYPE[proposal.action_type] ?? (humanizeToken(proposal.action_type) || 'Proposal');
  }
  return READABLE_RECOMMENDED_ACTION[action] ?? (humanizeToken(action) || 'Proposal');
}

export type MachineReasonExplanation = {
  summary: string;
  code: string | null;
  purpose: string | null;
};

// The machine's audit reason vocabulary (dev/machine auto_decide.py). Each decision
// carries one of these codes; the cockpit translates them to plain language and keeps
// the raw code visible as the audit reference.
export const REASON_SUMMARIES = {
  stage_0b_no_anchor_token_match:
    'The search term contains none of our product anchor words (keto, mct, c8, collagen, ketosource), so it cannot be a search for our catalog. Excluding it stops wasted spend.',
  stage_1_no_candidates:
    'No existing target group is a plausible home for this term, so the machine deferred it to a human.',
  suggestion_layer_outage:
    'The target-group suggestion service was down when this term was processed, so the machine deferred instead of guessing.',
  llm_outage:
    'The language-model check was unavailable, so the machine deferred instead of deciding without it.',
  ambiguous_profit_or_zero_spend:
    'Spend or profit data is missing or zero for this term, so the profit rules could not run.',
  negative_contribution_profit_low_volume:
    'This term loses money after margin and has low search volume (under 200), so cutting it is low-risk.',
  negative_contribution_profit_high_volume:
    'This term loses money after margin but has high search volume, so a human should weigh the trade-off.',
  positive_profit_below_target_bid_down_pending_d4_path:
    'Profitable, but below the profit target. A bid reduction (not an exclusion) is the likely fix.',
  no_negative_contribution_profit:
    'This term is not losing money after margin, so no exclusion is warranted.',
  cockpit_approved_exclusion_committed:
    'Exclusion approved in the cockpit and committed to the exclusion list.',
} as const satisfies Record<string, string>;

const REASON_PURPOSES = {
  stage_0b_no_anchor_token_match: 'Anchor-word check',
  stage_1_no_candidates: 'Target-group match check',
  suggestion_layer_outage: 'Suggestion service check',
  llm_outage: 'Language-model check',
  ambiguous_profit_or_zero_spend: 'Profit data check',
  negative_contribution_profit_low_volume: 'Margin check',
  negative_contribution_profit_high_volume: 'Margin check',
  positive_profit_below_target_bid_down_pending_d4_path: 'Profit-target check',
  no_negative_contribution_profit: 'Margin check',
  cockpit_approved_exclusion_committed: 'Exclusion committed',
} as const satisfies Record<keyof typeof REASON_SUMMARIES, string>;

// embedding_<similarity>_<verdict> codes from the D1 assignment path.
const EMBEDDING_REASON_PATTERN = /^embedding_([0-9.]+)_(.+)$/;

function embeddingSummary(code: string): string | null {
  const match = code.match(EMBEDDING_REASON_PATTERN);
  if (!match) {
    return null;
  }
  const [, sim, rest] = match;
  if (rest.endsWith('_llm_agrees')) {
    return `Similarity ${sim} to the closest target group is above the auto-assign threshold and the language-model check agrees.`;
  }
  if (rest === 'above_threshold_but_llm_disagrees') {
    return `Similarity ${sim} to the closest target group is above the threshold, but the language-model check disagrees, so it needs a human call.`;
  }
  if (rest === 'in_grey_band') {
    return `Similarity ${sim} to the closest target group falls in the grey band: too close to auto-exclude, too far to auto-assign.`;
  }
  if (rest === 'below_low_anchor_present') {
    return `Contains a product anchor word, but similarity ${sim} to every target group is below the floor, so it needs a human call.`;
  }
  return null;
}

// Translate a machine reason to operator language. Prose reasons (anything with
// whitespace) pass through untouched with no code. An approval-time match override
// appends "[match_type=PHRASE; was EXCLUDE_EXACT]" to the stored reason (cockpit-api
// writer.py); that suffix is folded into a readable sentence.
export function describeMachineReason(reason: string | null): MachineReasonExplanation | null {
  if (!reason || !reason.trim()) {
    return null;
  }
  const trimmed = reason.trim();
  const override = trimmed.match(/^(.*?)\s*\[match_type=(EXACT|PHRASE); was (\S+)\]$/);
  const base = (override ? override[1] : trimmed).trim();
  const overrideNote = override
    ? ` Operator set ${override[2].toLowerCase()} match at approval (machine recommended ${override[3].replace('EXCLUDE_', '').toLowerCase()}).`
    : '';
  if (!base) {
    return override ? { summary: overrideNote.trim(), code: null, purpose: null } : null;
  }
  if (/\s/.test(base)) {
    return { summary: base + overrideNote, code: null, purpose: null };
  }
  const mapped =
    REASON_SUMMARIES[base as keyof typeof REASON_SUMMARIES] ?? embeddingSummary(base);
  if (mapped) {
    const purpose = base in REASON_PURPOSES
      ? REASON_PURPOSES[base as keyof typeof REASON_PURPOSES]
      : EMBEDDING_REASON_PATTERN.test(base)
        ? 'Similarity check'
        : null;
    return { summary: mapped + overrideNote, code: base, purpose };
  }
  return { summary: `${humanizeToken(base)}.${overrideNote}`, code: base, purpose: null };
}

export function describeDecisionConsequence(
  proposal: Pick<MachineProposal, 'recommended_action'>,
): { approve: string; reject: string } | null {
  const action = (proposal.recommended_action ?? '').trim().toUpperCase();
  if (action === 'EXCLUDE_EXACT') {
    return {
      approve: "Approving adds this exact search term to the campaign's negative keywords, so ads stop showing for it.",
      reject: 'Rejecting leaves the term running. The machine will not raise it again.',
    };
  }
  if (action === 'EXCLUDE_PHRASE') {
    return {
      approve: "Approving adds this phrase and any search term containing it to the campaign's negative keywords, so ads stop showing for it.",
      reject: 'Rejecting leaves the term running. The machine will not raise it again.',
    };
  }
  return null;
}

// LANE SCOPE - the client mirror of reader.py::APPROVALS_SCOPES['review'] (see the note
// above isReviewProposal for why the lane scope and the waiting count are different rules).
// Proposals that belong to the Review queue's scope regardless of decision status:
// auto-recommend, non-maintain. Lets the review page keep already-approved / rejected /
// deferred items available behind a Status filter instead of dropping them on reload.
// The defer lane's equivalent (APPROVALS_SCOPES['defer'] = non-maintain + needs_review,
// status-agnostic as of KET-1434) is applied server-side via the `scope=defer` param; the
// Defer tab reaches decided rows through its Status filter, so no client twin is needed.
export function isReviewScope(
  proposal: Pick<MachineProposal, 'action_type' | 'recommended_action' | 'recommendation_tier'>,
): boolean {
  return proposal.recommendation_tier === 'auto_recommend' && !isMaintainProposal(proposal);
}

export function canApproveProposal(
  proposal: Pick<
    MachineProposal,
    'profit_effect' | 'action_type' | 'recommended_action' | 'recommendation_tier'
  >,
  context?: { selectedTargetGroup?: string | null },
): boolean {
  // KET-1717: the operator's own choice of target group IS the judgment the missing
  // profit forecast would have supplied, so an assign card is approvable at any tier
  // ONCE a target group is actually selected - and never before. This branch comes
  // FIRST on purpose: selection is the gate, so even an assign card that carried a
  // profit_effect must not approve unattended (fence 3).
  if (isTgAssignmentAssign(proposal)) {
    return (context?.selectedTargetGroup ?? '').trim() !== '';
  }
  if (proposal.profit_effect !== null) {
    return true;
  }
  // Cost-avoidance carve-out: an exclusion saves wasted spend, it does not generate units,
  // so it has no profit_effect to forecast. Approvable at auto_recommend tier only;
  // needs_review exclusions still route to the defer lane. Spend-increasing actions
  // (create_campaign, bid_change) are NOT exclusions and stay gated on profit_effect.
  return isCostAvoidanceExclusion(proposal) && proposal.recommendation_tier === 'auto_recommend';
}

export function filterProposals(proposals: MachineProposal[], filters: ProposalFilter): MachineProposal[] {
  const domino = filters.domino?.trim().toLowerCase();
  const market = filters.market?.trim().toLowerCase();
  const text = filters.text?.trim().toLowerCase();
  const status = filters.status?.trim().toLowerCase();

  return proposals.filter((proposal) => {
    if (domino && !proposalDominoMatches(proposal, domino)) {
      return false;
    }
    if (market && (proposal.market ?? '').toLowerCase() !== market) {
      return false;
    }
    if (status && (proposal.status ?? '').toLowerCase() !== status) {
      return false;
    }
    if (text) {
      const haystack = [proposal.keyword, proposal.recommended_target, proposal.target_group, proposal.asin, proposal.product, proposal.variant, proposal.reason]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(text)) {
        return false;
      }
    }
    return true;
  });
}

export function sortProposals(proposals: MachineProposal[], key: ProposalSortKey, dir: SortDirection): MachineProposal[] {
  return [...proposals].sort((left, right) => {
    if (key === 'impact') {
      return compareValues(left.profit_effect, right.profit_effect, dir);
    }
    if (key === 'domino') {
      return compareValues(proposalDominoPrefix(left), proposalDominoPrefix(right), dir);
    }
    return compareValues(left[key], right[key], dir);
  });
}

export function sortRows<T extends Record<string, unknown>>(rows: T[], key: keyof T | string, dir: SortDirection): T[] {
  return [...rows].sort((left, right) => compareValues(left[key], right[key], dir));
}

export function distinctValues<T extends Record<string, unknown>>(rows: T[], key: keyof T | string): string[] {
  const values = new Set<string>();
  for (const row of rows) {
    const value = row[key];
    const items = Array.isArray(value) ? value : [value];
    for (const item of items) {
      if (isMissingSortValue(item)) {
        continue;
      }
      values.add(String(item));
    }
  }
  return [...values].sort((left, right) => left.localeCompare(right, undefined, { numeric: true, sensitivity: 'base' }));
}

export function approveDisabledReason(
  proposal: Pick<
    MachineProposal,
    'profit_effect' | 'action_type' | 'recommended_action' | 'recommendation_tier'
  >,
  context?: { selectedTargetGroup?: string | null },
): string | null {
  if (canApproveProposal(proposal, context)) {
    return null;
  }
  if (isTgAssignmentAssign(proposal)) {
    return 'Select a target group to approve.';
  }
  if (isCostAvoidanceExclusion(proposal) && proposal.recommendation_tier === 'needs_review') {
    return 'Routed to defer-to-human.';
  }
  return 'Waiting for profit signal.';
}

export function statusChip(status: ProposalStatus): { label: string; tone: StatusChipTone; icon: string } {
  const normalized = status.toLowerCase();
  const known: Record<string, { label: string; tone: StatusChipTone; icon: string }> = {
    proposed: { label: 'PROPOSED', tone: 'purple', icon: '●' },
    approved: { label: 'APPROVED', tone: 'ok', icon: '✓' },
    rejected: { label: 'REJECTED', tone: 'bad', icon: '✕' },
    deferred: { label: 'DEFERRED', tone: 'warn', icon: '!' },
    withdrawn: { label: 'WITHDRAWN', tone: 'neutral', icon: '○' },
    executing: { label: 'EXECUTING', tone: 'warn', icon: '●' },
    executed: { label: 'EXECUTED', tone: 'ok', icon: '✓' },
    failed: { label: 'FAILED', tone: 'bad', icon: '✕' },
  };
  return known[normalized] ?? { label: status, tone: 'neutral', icon: '○' };
}

export function confidenceTierChip(tier: ConfidenceTier | null): { label: string; tone: StatusChipTone } | null {
  if (!tier) {
    return null;
  }
  const known: Record<string, { label: string; tone: StatusChipTone }> = {
    measured: { label: 'measured', tone: 'ok' },
    estimated: { label: 'estimated', tone: 'warn' },
    'forecast-low': { label: 'forecast, low confidence', tone: 'warn' },
  };
  return known[tier] ?? { label: tier, tone: 'neutral' };
}

export function sectionAvailability(raw: unknown): SectionAvailability {
  const record = isRecord(raw) ? raw : {};
  return {
    approvals: Array.isArray(record.approvals),
    objectives: Array.isArray(record.objectives) || isRecord(record.objectives),
    config: isRecord(record.config),
    intake_freshness: Array.isArray(record.intake_freshness),
    stats: isRecord(record.stats),
    experiments: Array.isArray(record.experiments),
    publish_readback: 'publish_readback' in record,
  };
}

export function parseDecisionState(raw: unknown, status = 200): MachineDecisionState {
  const record = isRecord(raw) ? raw : {};
  return {
    approvals: parseProposals(record.approvals),
    objectives: isRecord(record.objectives)
      ? parseObjectives(record.objectives)
      : { summary: [], detail: [], window: null },
    config: parseConfig(record.config),
    intake_freshness: parseFreshness(record.intake_freshness),
    stats: parseStats(record.stats),
    experiments: Array.isArray(record.experiments) ? record.experiments.filter(isRecord) : null,
    publish_readback: 'publish_readback' in record ? record.publish_readback : null,
    error: typeof record.error === 'string' ? record.error : null,
    status,
  };
}

// Match type is structurally exclusive from create-campaign and target-group overrides.
export function buildApprovalPayload(
  proposal_id: string,
  decision: ApprovalDecision,
  reason?: string | null,
  matchType?: Extract<ProposalMatchType, 'exact' | 'phrase'> | null,
  substituteAction?: 'ASSIGN' | 'EXCLUDE' | 'EXCLUDE_EXACT' | null,
  targetGroup?: string | null,
  campaignTemplate?: string | null,
  asin?: string | null,
): {
  proposal_id: string;
  decision: ApprovalDecision;
  reason: string | null;
  match_type?: 'EXACT' | 'PHRASE';
  substitute_action?: 'ASSIGN' | 'EXCLUDE' | 'EXCLUDE_EXACT';
  target_group?: string;
  campaign_template?: string;
  asin?: string;
} {
  const payload: {
    proposal_id: string;
    decision: ApprovalDecision;
    reason: string | null;
    match_type?: 'EXACT' | 'PHRASE';
    substitute_action?: 'ASSIGN' | 'EXCLUDE' | 'EXCLUDE_EXACT';
    target_group?: string;
    campaign_template?: string;
    asin?: string;
  } = { proposal_id, decision, reason: reason?.trim() || null };
  const legalSubstitute =
    substituteAction === 'ASSIGN' ||
    substituteAction === 'EXCLUDE' ||
    substituteAction === 'EXCLUDE_EXACT';
  // The approval API rejects match_type and substitute_action together. A legal
  // substitution is therefore authoritative and makes the builder unable to
  // produce the invalid pair, even if a caller still holds a stale match choice.
  if (decision === 'approved' && legalSubstitute) {
    payload.substitute_action = substituteAction;
  } else if (decision === 'approved' && (matchType === 'exact' || matchType === 'phrase')) {
    payload.match_type = matchType.toUpperCase() as 'EXACT' | 'PHRASE';
  }
  if (decision === 'approved' && typeof targetGroup === 'string' && targetGroup.trim()) {
    payload.target_group = targetGroup.trim();
  }
  if (decision === 'approved' && typeof campaignTemplate === 'string' && campaignTemplate.trim()) {
    payload.campaign_template = campaignTemplate.trim();
  }
  if (decision === 'approved' && typeof asin === 'string' && asin.trim()) {
    payload.asin = asin.trim();
  }
  return payload;
}

export function buildProposalDuplicatePayload(
  sourceProposalId: string,
  asin: string,
  market?: string | null,
  campaignTemplate?: string | null,
  targetGroup?: string | null,
): {
  source_proposal_id: string;
  asin: string;
  market?: string;
  campaign_template?: string;
  target_group?: string;
} | null {
  const source = sourceProposalId.trim();
  const product = asin.trim();
  if (!source || !product) {
    return null;
  }
  return {
    source_proposal_id: source,
    asin: product,
    ...(market?.trim() ? { market: market.trim() } : {}),
    ...(campaignTemplate?.trim() ? { campaign_template: campaignTemplate.trim() } : {}),
    ...(targetGroup?.trim() ? { target_group: targetGroup.trim() } : {}),
  };
}

export function describeTargetGroupProvenance(value: unknown): string | null {
  if (value === 'assigned') return 'Already in this group';
  if (value === 'suggested') return 'Machine suggested';
  return null;
}

export function productsForMarket(products: ProductOption[], market: string | null | undefined): ProductOption[] {
  const normalizedMarket = market?.trim().toLowerCase();
  if (!normalizedMarket) return [];
  return products.filter((product) => product.market.trim().toLowerCase() === normalizedMarket);
}

export function buildKillPayload(state: MachineStatus, reason?: string | null): {
  kind: 'kill';
  state: MachineStatus;
  reason: string | null;
} {
  return { kind: 'kill', state, reason: reason?.trim() || null };
}

export function buildDominoOrderPayload(domino_order: string[]): { kind: 'domino_order'; domino_order: string[] } {
  return { kind: 'domino_order', domino_order };
}

export function buildDominoThresholdsPayload(domino_thresholds: Record<string, unknown>): {
  kind: 'domino_thresholds';
  domino_thresholds: Record<string, unknown>;
} {
  return { kind: 'domino_thresholds', domino_thresholds };
}

export function buildDominoRegistry(state: Pick<MachineDecisionState, 'stats'>): DominoRegistryEntry[] {
  if (!state.stats) {
    return [];
  }

  const entries = new Map<string, DominoRegistryEntry>();
  for (const row of state.stats.mece_coverage) {
    const label = typeof row.domino === 'string' ? row.domino.trim() : '';
    const bucketId = typeof row.bucket_id === 'string' ? row.bucket_id.trim() : '';
    const id = bucketId || dominoIdFromLabel(label);
    if (!id || !label || entries.has(id)) {
      continue;
    }
    entries.set(id, { id, label });
  }

  return [...entries.values()].sort((left, right) => dominoSortValue(left.id) - dominoSortValue(right.id));
}

export function objectiveTypeKey(objective: string, campaignType: string | null): string {
  return `${objective}||${campaignType ?? ''}`;
}

export function groupObjectiveDetail(detail: ObjectiveDetailRow[]): Map<string, Map<string, ObjectiveDetailRow[]>> {
  const grouped = new Map<string, Map<string, ObjectiveDetailRow[]>>();
  for (const row of detail) {
    const groupKey = objectiveTypeKey(row.objective, row.campaign_type);
    const objectiveGroup = grouped.get(groupKey) ?? new Map<string, ObjectiveDetailRow[]>();
    const marketRows = objectiveGroup.get(row.market) ?? [];
    marketRows.push(row);
    objectiveGroup.set(row.market, marketRows);
    grouped.set(groupKey, objectiveGroup);
  }
  return grouped;
}

export function mergeDominoOrder(configured: string[] | null, registry: DominoRegistryEntry[]): DominoOrderEntry[] {
  const registryById = new Map(registry.map((entry) => [entry.id, entry]));
  const registryByNormalizedId = new Map(registry.map((entry) => [normalizeDominoId(entry.id), entry]));
  const result: DominoOrderEntry[] = [];
  const seen = new Set<string>();

  for (const rawId of configured ?? []) {
    const id = rawId.trim();
    if (!id) {
      continue;
    }
    const match = registryById.get(id) ?? registryByNormalizedId.get(normalizeDominoId(id));
    const output = match ? { id: match.id, label: match.label } : { id, label: null };
    const seenKey = normalizeDominoId(output.id);
    if (seen.has(seenKey)) {
      continue;
    }
    result.push(output);
    seen.add(seenKey);
  }

  for (const entry of registry) {
    const seenKey = normalizeDominoId(entry.id);
    if (seen.has(seenKey)) {
      continue;
    }
    result.push({ id: entry.id, label: entry.label });
    seen.add(seenKey);
  }

  return result;
}

export function parseProposals(value: unknown): MachineProposal[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((row) => ({
    proposal_id: stringValue(row.proposal_id, 'unknown'),
    domino: stringValue(row.domino, 'n/a'),
    action_type: stringValue(row.action_type, 'Proposal'),
    keyword: nullableString(row.keyword),
    market: nullableString(row.market),
    asin: nullableString(row.asin),
    product: nullableString(row.product),
    variant: nullableString(row.variant),
    target_group: nullableString(row.target_group),
    ...('target_group_provenance' in row
      ? {
          target_group_provenance:
            row.target_group_provenance === 'assigned' || row.target_group_provenance === 'suggested'
              ? row.target_group_provenance
              : null,
        }
      : {}),
    search_volume: nullableNumber(row.search_volume),
    recommended_action: stringValue(row.recommended_action, stringValue(row.action_type, 'Proposal')),
    recommended_target: nullableString(row.recommended_target),
    recommendation_tier: stringValue(row.recommendation_tier, 'needs_review'),
    units_effect: nullableString(row.units_effect),
    profit_effect: nullableNumber(row.profit_effect),
    reason: nullableString(row.reason),
    confidence: nullableNumber(row.confidence),
    confidence_tier: nullableString(row.confidence_tier),
    status: stringValue(row.status, 'proposed'),
    updated_at: nullableString(row.updated_at),
    operator_note: nullableString(row.operator_note),
    note_updated_by: nullableString(row.note_updated_by),
    note_updated_at: nullableString(row.note_updated_at),
  }));
}

// B4 server-driven approvals page: scope narrows to a tab's queue (SQL ports of
// isReviewScope / isDeferProposal / isMaintainProposal live in cockpit-api reader.py),
// the other keys mirror the client filter fields, and limit/offset page through.
export type ApprovalsScope = 'review' | 'defer' | 'maintain';

export type ApprovalsQuery = {
  scope?: ApprovalsScope;
  status?: string;
  domino?: string;
  market?: string;
  action?: string;
  q?: string;
  limit?: number;
  offset?: number;
};

export type ApprovalsFacets = { dominoes: string[]; markets: string[]; actions: string[] };

export type ApprovalsPage = {
  items: MachineProposal[];
  total: number;
  limit: number;
  offset: number;
  facets: ApprovalsFacets;
};

export function parseApprovalsPage(raw: unknown): ApprovalsPage {
  const record = isRecord(raw) ? raw : {};
  const facets = isRecord(record.facets) ? record.facets : {};
  return {
    items: parseProposals(record.items),
    total: Number(record.total) || 0,
    limit: Number(record.limit) || 0,
    offset: Number(record.offset) || 0,
    facets: {
      dominoes: parseStringArray(facets.dominoes),
      markets: parseStringArray(facets.markets),
      actions: parseStringArray(facets.actions),
    },
  };
}

function parseStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function parseBadges(raw: unknown): { review: number; defer: number; maintain: number } {
  const record = isRecord(raw) ? raw : {};
  return {
    review: Number(record.review) || 0,
    defer: Number(record.defer) || 0,
    maintain: Number(record.maintain) || 0,
  };
}

export function parseObjectives(value: unknown): ObjectivesSection {
  if (!isRecord(value)) {
    throw new TypeError('Objectives payload must be an object');
  }
  return {
    summary: parseObjectiveSummary(value.summary),
    detail: parseObjectiveDetail(value.detail),
    window: parseObjectiveWindow(value.window),
  };
}

function parseObjectiveSummary(value: unknown): ObjectiveSummaryRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((row) => ({
    objective: stringValue(row.objective, OBJECTIVE_NULL_BUCKET_LABEL),
    objective_tag: nullableString(row.objective_tag),
    campaign_type: nullableString(row.campaign_type),
    campaigns_total: nullableNumber(row.campaigns_total),
    markets: nullableString(row.markets),
    spend: nullableNumber(row.spend),
    sales: nullableNumber(row.sales),
    clicks: nullableNumber(row.clicks),
    profit: nullableNumber(row.profit),
    profit_yield: nullableNumber(row.profit_yield),
    acos: nullableNumber(row.acos),
    margin_imputed_share: nullableNumber(row.margin_imputed_share),
    margin_unknown_share: nullableNumber(row.margin_unknown_share),
    conforming: nullableNumber(row.conforming),
    judged: nullableNumber(row.judged),
    band: parseBand(row.band),
  }));
}

function parseObjectiveDetail(value: unknown): ObjectiveDetailRow[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(isRecord).map((row) => ({
    objective: stringValue(row.objective, OBJECTIVE_NULL_BUCKET_LABEL),
    campaign_type: nullableString(row.campaign_type),
    market: stringValue(row.market, 'All markets'),
    account: nullableString(row.account),
    campaign_name: stringValue(row.campaign_name, 'Unnamed campaign'),
    is_live: Boolean(row.is_live),
    acos: nullableNumber(row.acos),
    spend: nullableNumber(row.spend),
    sales: nullableNumber(row.sales),
    profit: nullableNumber(row.profit),
    profit_yield: nullableNumber(row.profit_yield),
    margin_unknown_share: nullableNumber(row.margin_unknown_share),
    conformance_status: nullableString(row.conformance_status),
    conformance_reasons: Array.isArray(row.conformance_reasons)
      ? row.conformance_reasons.filter((reason): reason is string => typeof reason === 'string')
      : nullableString(row.conformance_reasons) ? [String(row.conformance_reasons)] : [],
    band: parseBand(row.band),
  }));
}

function parseObjectiveWindow(value: unknown): ObjectiveWindow | null {
  if (!isRecord(value)) return null;
  const start = nullableString(value.start);
  const end = nullableString(value.end);
  const maxFactDate = nullableString(value.max_fact_date);
  const attributionMatureThrough = nullableString(value.attribution_mature_through);
  if (!start || !end || !maxFactDate || !attributionMatureThrough) return null;
  return {
    start,
    end,
    max_fact_date: maxFactDate,
    attribution_mature_through: attributionMatureThrough,
  };
}

export function parseBand(value: unknown): ObjectiveBand {
  return value === 'green' || value === 'amber' || value === 'red' ? value : 'unknown';
}

export function parseConfig(value: unknown): MachineConfig | null {
  if (!isRecord(value)) {
    return null;
  }
  const killState = value.kill_state === 'OFF' ? 'OFF' : 'ON';
  const updatedByKey = ['updated', 'by'].join('_');
  const meta = isRecord(value.kill_meta)
    ? {
        updated_at: nullableString(value.kill_meta.updated_at),
        updatedBy: nullableString(value.kill_meta[updatedByKey]),
        reason: nullableString(value.kill_meta.reason),
      }
    : null;
  return {
    kill_state: killState,
    kill_meta: meta,
    domino_order: Array.isArray(value.domino_order) ? value.domino_order.filter((item): item is string => typeof item === 'string') : [],
    domino_thresholds: isRecord(value.domino_thresholds) ? value.domino_thresholds : {},
    ...(Array.isArray(value.campaign_templates)
      ? {
          campaign_templates: value.campaign_templates.filter(
            (item): item is string => typeof item === 'string',
          ),
        }
      : {}),
    ...('campaign_template_default' in value
      ? { campaign_template_default: nullableString(value.campaign_template_default) }
      : {}),
    ...(Array.isArray(value.campaign_template_catalogue)
      ? { campaign_template_catalogue: parseCampaignTemplateCatalogue(value.campaign_template_catalogue) }
      : {}),
    ...(isRecord(value.campaign_template_meta)
      ? {
          campaign_template_meta: {
            ...(typeof value.campaign_template_meta.source === 'string'
              ? { source: value.campaign_template_meta.source }
              : {}),
            ...('updated_at' in value.campaign_template_meta
              ? { updated_at: nullableString(value.campaign_template_meta.updated_at) }
              : {}),
            ...('updated_by' in value.campaign_template_meta
              ? { updated_by: nullableString(value.campaign_template_meta.updated_by) }
              : {}),
          },
        }
      : {}),
  };
}

function parseCampaignTemplateCatalogue(value: unknown[]): CampaignTemplate[] {
  const requiredStrings = [
    'name',
    'campaign_type',
    'funnel_stage',
    'match_type',
    'placement',
    'bidding_strategy',
    'si_objective',
  ] as const;
  const templates: CampaignTemplate[] = [];
  for (const item of value) {
    if (!isRecord(item)) continue;
    if (requiredStrings.some((key) => typeof item[key] !== 'string' || item[key].length === 0)) continue;
    if (typeof item.placement_percentage !== 'number' || !Number.isFinite(item.placement_percentage)) continue;
    if (typeof item.default_budget !== 'number' || !Number.isFinite(item.default_budget)) continue;
    templates.push({
      name: item.name as string,
      campaign_type: item.campaign_type as string,
      funnel_stage: item.funnel_stage as string,
      match_type: item.match_type as string,
      placement: item.placement as string,
      placement_percentage: item.placement_percentage,
      bidding_strategy: item.bidding_strategy as string,
      default_budget: item.default_budget,
      si_objective: item.si_objective as string,
      ...(item.retired === true ? { retired: true } : {}),
      ...(typeof item.description === 'string' ? { description: item.description } : {}),
    });
  }
  return templates;
}

function parseFreshness(value: unknown): IntakeFreshness[] | null {
  if (!Array.isArray(value)) {
    return null;
  }
  return value.filter(isRecord).map((row) => ({
    leg: stringValue(row.leg, 'unknown'),
    last_at: nullableString(row.last_at),
    stale: Boolean(row.stale),
  }));
}

export function parseStats(value: unknown): MachineStats | null {
  if (!isRecord(value)) {
    return null;
  }
  return {
    mece_coverage: Array.isArray(value.mece_coverage) ? value.mece_coverage.filter(isRecord) : [],
    tg_summary: Array.isArray(value.tg_summary) ? value.tg_summary.filter(isRecord) : [],
  };
}

// state/experiments returns a bare array (safe default [] on the cockpit-api side).
// Non-array shapes coerce to an empty list so the tab shows its empty state, never throws.
export function parseExperiments(value: unknown): MachineExperiment[] {
  return Array.isArray(value) ? value.filter(isRecord) : [];
}

function parseInterventions(value: unknown): TacticIntervention[] {
  let candidate = value;
  if (typeof value === 'string') {
    try {
      candidate = JSON.parse(value) as unknown;
    } catch {
      return [];
    }
  }
  if (!Array.isArray(candidate)) return [];
  return candidate.filter(isRecord).flatMap((row): TacticIntervention[] => {
    const interventionType = nullableString(row.intervention_type);
    if (!interventionType || !isRecord(row.params)) return [];
    return [{ intervention_type: interventionType, params: { ...row.params } }];
  });
}

function parseTacticVersions(value: unknown): TacticVersion[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): TacticVersion[] => {
    const version = nullableNumber(row.version);
    if (version === null) return [];
    return [{
      version,
      is_current: Boolean(row.is_current),
      interventions: parseInterventions(row.interventions),
      created_by: nullableString(row.created_by),
      created_at: nullableString(row.created_at),
    }];
  });
}

// state/tactics returns each tactic identity with its complete immutable version history.
// The backend currently derives current/version counts from the versions array, while the
// parser also accepts explicit aggregate fields for forward compatibility.
export function parseTactics(value: unknown): TacticDefinition[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): TacticDefinition[] => {
    const tacticId = nullableString(row.tactic_id);
    if (!tacticId) return [];
    const versions = parseTacticVersions(row.versions);
    const current = versions.find((version) => version.is_current);
    return [{
      tactic_id: tacticId,
      name: stringValue(row.name, tacticId),
      description: nullableString(row.description),
      status: stringValue(row.status, 'unknown'),
      created_by: nullableString(row.created_by),
      created_at: nullableString(row.created_at),
      updated_at: nullableString(row.updated_at),
      version_count: nullableNumber(row.version_count) ?? versions.length,
      current_version: nullableNumber(row.current_version) ?? current?.version ?? versions[0]?.version ?? 0,
      versions,
      signal_ids: parseStringArray(row.signal_ids ?? row.where_used),
    }];
  });
}

function parseTacticDrafts(value: unknown): TacticDraft[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): TacticDraft[] => {
    const draftId = nullableString(row.draft_id);
    const tacticId = nullableString(row.tactic_id);
    const signalId = nullableString(row.signal_id);
    const itemKey = nullableString(row.item_key);
    if (!draftId || !tacticId || !signalId || !itemKey) return [];
    return [{
      draft_id: draftId,
      tactic_id: tacticId,
      tactic_version: nullableNumber(row.tactic_version),
      signal_id: signalId,
      item_key: itemKey,
      keyword: stringValue(row.keyword, 'Unknown keyword'),
      market: stringValue(row.market, 'Unknown market'),
      asin: nullableString(row.asin),
      delta_kind: stringValue(row.delta_kind, 'unknown'),
      action_type: stringValue(row.action_type, 'unknown'),
      value_model: stringValue(row.value_model, 'unknown'),
      expected_delta_value: nullableNumber(row.expected_delta_value),
      confidence_tier: stringValue(row.confidence_tier, 'unknown'),
      value_basis: stringValue(row.value_basis, 'unknown'),
      is_spend_increasing: Boolean(row.is_spend_increasing),
      item_value: nullableNumber(row.item_value),
      band_rank: nullableNumber(row.band_rank),
      resolved_interventions: parseInterventions(row.resolved_interventions),
      status: stringValue(row.status, 'draft'),
    }];
  });
}

export function parseTacticDraftsPage(raw: unknown): TacticDraftsPage {
  const record = isRecord(raw) ? raw : {};
  return {
    items: parseTacticDrafts(record.items),
    total: Number(record.total) || 0,
    limit: Number(record.limit) || 0,
    offset: Number(record.offset) || 0,
  };
}

export function tacticDraftAllowsExperiment(
  draft: Pick<TacticDraft, 'value_model' | 'expected_delta_value'>,
): boolean {
  return draft.expected_delta_value !== null && draft.value_model.trim().length > 0 && draft.value_model !== 'unpriced';
}

export function formatTacticDraftValue(value: number | null): string {
  return value === null ? 'not priced' : `£${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 }).format(value)}`;
}

export function describeTacticDraftFix(
  draft: Pick<TacticDraft, 'resolved_interventions' | 'action_type'>,
): { title: string; params: Array<[string, string]> } {
  const intervention = draft.resolved_interventions[0];
  if (!intervention) return { title: humanizeToken(draft.action_type) || 'No resolved fix', params: [] };
  return {
    title: humanizeToken(intervention.intervention_type),
    params: Object.entries(intervention.params).map(([key, value]) => [humanizeToken(key), String(value)]),
  };
}

export function tacticDraftConflictMessage(body: Record<string, unknown>): string {
  const current = body.current_version
    ?? body.current_tactic_version
    ?? (isRecord(body.current) ? body.current.version : undefined);
  const detail = typeof body.detail === 'string' ? body.detail : null;
  if (current !== undefined && current !== null) return `Draft superseded. Current tactic version: ${String(current)}.`;
  return detail ?? 'Draft superseded by a newer tactic version.';
}

// Whether the application's mutation actually reached Amazon (KET-1114). This is
// NOT the ledger `current_status`: Apply is ledger-only, so an `active` row is
// `not_published` until the explicit publish leg runs. `unknown` means the reader
// could not read the publish journal; never treat it as "nothing was published".
// Vocabulary mirrors machine/cockpit_api/reader.py PUBLISH_STATE_*.
export type PublishState = 'published' | 'not_published' | 'reversed' | 'unknown';

const PUBLISH_STATES: readonly PublishState[] = ['published', 'not_published', 'reversed', 'unknown'];

// One application of a tactic to an item. `via` is 'item' for a directly-scoped application
// and 'group' when the item inherits it from a group application.
export type TacticApplication = {
  application_id: string;
  tactic_id: string;
  tactic_name: string;
  current_status: string;
  via: string;
  source: string | null;
  applied_at: string | null;
  event_type: string;
  tactic_version: number | null;
  // Amazon truth, from the publish journal. A reader that predates KET-1114 (or
  // any payload without the field) reads as `unknown`, not as a false
  // `not_published` and never as a false `published`.
  publish_state: PublishState;
  publish_target_ref: string | null;
  published_at: string | null;
  publish_failure_reason: string | null;
};

export type ItemOutcomePoint = {
  observed_at: string;
  profit_yield: number | null;
  units_sold: number | null;
};

// Last-measured search-term economics for the item (W1d). `source` says which basis:
// 'decision_snapshot' is the W1c block captured when the tactic was decided;
// 'latest_observation' is the last-known observation from the append-only history.
// A null block means NOT MEASURED, never "zero spend".
export type ItemEconomics = {
  spend: number | null;
  sales: number | null;
  clicks: number | null;
  orders: number | null;
  impressions: number | null;
  window_start: string | null;
  window_end: string | null;
  as_of: string | null;
  source: 'decision_snapshot' | 'latest_observation';
};

export type ItemStack = {
  item_key: string;
  keyword: string;
  market: string;
  asin: string | null;
  // Best-effort product/variant stamp from the reader (same lookup the approvals
  // cards use). Null degrades the cell to the bare ASIN, never blanks it.
  product: string | null;
  variant: string | null;
  // Margin-adjusted profit per unit of spend (TG-summary definition). Null = not
  // measured (sparse by construction: negated items have no profit row).
  profit_yield: number | null;
  // Everything not retired: the tactics currently in force on the item. A `matured` negation
  // still holds (the negative keyword is still in place), so it belongs here.
  active_stack: TacticApplication[];
  application_timeline: TacticApplication[];
  item_economics: ItemEconomics | null;
  outcome_series: ItemOutcomePoint[];
};

export type ItemStatus = Omit<ItemStack, 'application_timeline' | 'outcome_series'> & {
  target_group: string | null;
  depth: number;
  campaign_state: string | null;
  objective: string | null;
  in_universe: boolean;
  has_tactic: boolean;
  has_multiple_item_keys: boolean;
  contribution_profit: number | null;
  last_applied: string | null;
};

export type ChangeLogEvent = {
  application_id: string;
  item_key: string;
  keyword: string;
  market: string;
  asin: string | null;
  product: string | null;
  variant: string | null;
  tactic_id: string;
  tactic_name: string;
  tactic_version: number | null;
  event_status: string;
  source: string | null;
  via: string;
  applied_by: string | null;
  applied_at: string | null;
  publish_state: PublishState;
  target_ref: string | null;
  published_at: string | null;
  publish_failure_reason: string | null;
  note: string | null;
  override_json: string | null;
};

function parseApplication(value: unknown): TacticApplication | null {
  if (!isRecord(value)) return null;
  const applicationId = nullableString(value.application_id);
  const tacticId = nullableString(value.tactic_id);
  if (!applicationId || !tacticId) return null;
  return {
    application_id: applicationId,
    tactic_id: tacticId,
    // The view left-joins the tactic vocabulary, so a name can be absent. Fall back to the id
    // rather than rendering a blank cell.
    tactic_name: stringValue(value.tactic_name, tacticId),
    current_status: stringValue(value.current_status, 'unknown'),
    via: stringValue(value.via, 'item'),
    source: nullableString(value.source),
    applied_at: nullableString(value.applied_at ?? value.event_at ?? value.occurred_at),
    event_type: stringValue(value.event_type ?? value.action ?? value.operation ?? value.current_status, 'unknown'),
    tactic_version: nullableNumber(value.tactic_version),
    publish_state: parsePublishState(value.publish_state),
    publish_target_ref: nullableString(value.publish_target_ref),
    published_at: nullableString(value.published_at),
    publish_failure_reason: nullableString(value.publish_failure_reason),
  };
}

function parsePublishState(value: unknown): PublishState {
  return PUBLISH_STATES.includes(value as PublishState) ? (value as PublishState) : 'unknown';
}

function parseApplications(value: unknown): TacticApplication[] {
  if (!Array.isArray(value)) return [];
  return value
    .map(parseApplication)
    .filter((application): application is TacticApplication => application !== null);
}

function parseItemEconomics(value: unknown): ItemEconomics | null {
  if (!isRecord(value)) return null;
  const source = value.source;
  // An unknown source basis is unusable: rather than mislabel it, treat as not measured.
  if (source !== 'decision_snapshot' && source !== 'latest_observation') return null;
  return {
    spend: nullableNumber(value.spend),
    sales: nullableNumber(value.sales),
    clicks: nullableNumber(value.clicks),
    orders: nullableNumber(value.orders),
    impressions: nullableNumber(value.impressions),
    window_start: nullableString(value.window_start),
    window_end: nullableString(value.window_end),
    as_of: nullableString(value.as_of),
    source,
  };
}

function parseOutcomeSeries(value: unknown): ItemOutcomePoint[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((point): ItemOutcomePoint[] => {
    const observedAt = nullableString(point.observed_at ?? point.as_of ?? point.date ?? point.timestamp);
    if (!observedAt) return [];
    const profitYield = nullableNumber(point.profit_yield);
    const unitsSold = nullableNumber(point.units_sold);
    if (profitYield === null && unitsSold === null) return [];
    return [{ observed_at: observedAt, profit_yield: profitYield, units_sold: unitsSold }];
  });
}

// state/item-stacks returns a bare array. `contribution_profit` is intentionally NOT surfaced:
// it is populated for a small minority of items (a negated term has no profit row by
// construction), so a column for it would read as "no profit" instead of "not measured".
// `item_economics` (W1d) IS surfaced: a null block renders as "not measured", so the
// NULL-stub trap the W1c deferral guarded against does not apply.
export function parseItemStacks(value: unknown): ItemStack[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): ItemStack[] => {
    const itemKey = nullableString(row.item_key);
    const keyword = nullableString(row.keyword);
    if (!itemKey || !keyword) return [];
    const economicsRecord = isRecord(row.item_economics) ? row.item_economics : null;
    const explicitOutcomeSeries = parseOutcomeSeries(
      row.outcome_series ?? economicsRecord?.outcome_series ?? economicsRecord?.series,
    );
    return [
      {
        item_key: itemKey,
        keyword,
        market: stringValue(row.market, 'unknown'),
        asin: nullableString(row.asin),
        product: nullableString(row.product),
        variant: nullableString(row.variant),
        profit_yield: nullableNumber(row.profit_yield),
        active_stack: parseApplications(row.active_stack),
        application_timeline: parseApplications(row.application_timeline),
        item_economics: parseItemEconomics(row.item_economics),
        outcome_series: explicitOutcomeSeries.length > 0
          ? explicitOutcomeSeries
          : parseOutcomeSeries(economicsRecord ? [economicsRecord] : []),
      },
    ];
  });
}

export function parseItemStatus(value: unknown): ItemStatus[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): ItemStatus[] => {
    const itemKey = nullableString(row.item_key);
    const keyword = nullableString(row.keyword);
    if (!itemKey || !keyword) return [];
    const activeStack = parseApplications(row.active_stack);
    return [{
      item_key: itemKey,
      keyword,
      market: stringValue(row.market, 'unknown'),
      asin: nullableString(row.asin),
      product: nullableString(row.product),
      variant: nullableString(row.variant),
      target_group: nullableString(row.target_group),
      depth: nullableNumber(row.depth) ?? 0,
      active_stack: activeStack,
      campaign_state: nullableString(row.campaign_state),
      objective: nullableString(row.objective),
      in_universe: row.in_universe !== false,
      has_tactic: row.has_tactic === true,
      has_multiple_item_keys: row.has_multiple_item_keys === true,
      item_economics: parseItemEconomics(row.item_economics),
      contribution_profit: nullableNumber(row.contribution_profit),
      profit_yield: nullableNumber(row.profit_yield),
      last_applied: nullableString(row.last_applied),
    }];
  });
}

export function parseChangeLog(value: unknown): ChangeLogEvent[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): ChangeLogEvent[] => {
    const applicationId = nullableString(row.application_id);
    const itemKey = nullableString(row.item_key);
    const keyword = nullableString(row.keyword);
    const tacticId = nullableString(row.tactic_id);
    if (!applicationId || !itemKey || !keyword || !tacticId) return [];
    return [{
      application_id: applicationId,
      item_key: itemKey,
      keyword,
      market: stringValue(row.market, 'unknown'),
      asin: nullableString(row.asin),
      product: nullableString(row.product),
      variant: nullableString(row.variant),
      tactic_id: tacticId,
      tactic_name: stringValue(row.tactic_name, tacticId),
      tactic_version: nullableNumber(row.tactic_version),
      event_status: stringValue(row.event_status, 'unknown'),
      source: nullableString(row.source),
      via: stringValue(row.via, 'item'),
      applied_by: nullableString(row.applied_by),
      applied_at: nullableString(row.applied_at),
      publish_state: parsePublishState(row.publish_state),
      target_ref: nullableString(row.target_ref),
      published_at: nullableString(row.published_at),
      publish_failure_reason: nullableString(row.publish_failure_reason),
      note: nullableString(row.note),
      override_json: nullableString(row.override_json),
    }];
  });
}

export function parseTacticExperiments(value: unknown): TacticExperiment[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRecord).flatMap((row): TacticExperiment[] => {
    const experimentId = nullableString(row.experiment_id);
    const applicationId = nullableString(row.application_id);
    const tacticId = nullableString(row.tactic_id);
    const itemKey = nullableString(row.item_key);
    if (!experimentId || !applicationId || !tacticId || !itemKey) return [];
    const result = row.result === 'PASS' || row.result === 'FAIL' || row.result === 'PENDING'
      ? row.result
      : 'PENDING';
    const rawTimeUnderTest = row.time_under_test ?? row.time_under_test_hours;
    const timeUnderTest = typeof rawTimeUnderTest === 'string' || typeof rawTimeUnderTest === 'number'
      ? rawTimeUnderTest
      : null;
    const confidence = typeof row.confidence === 'string' || typeof row.confidence === 'number'
      ? row.confidence
      : null;
    return [{
      experiment_id: experimentId,
      application_id: applicationId,
      tactic_id: tacticId,
      treatment: nullableString(row.treatment),
      item_key: itemKey,
      keyword: stringValue(row.keyword, 'Unknown keyword'),
      market: stringValue(row.market, 'Unknown market'),
      asin: nullableString(row.asin),
      value_model: stringValue(row.value_model, 'unknown'),
      delta_kind: stringValue(row.delta_kind, 'unknown'),
      state: stringValue(row.state, 'unknown'),
      trial_start: nullableString(row.trial_start),
      time_under_test: timeUnderTest,
      time_under_test_unit: row.time_under_test === undefined && row.time_under_test_hours !== undefined ? 'hours' : null,
      matured: Boolean(row.matured),
      result,
      confidence,
    }];
  });
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown, fallback: string): string {
  return typeof value === 'string' && value.length > 0 ? value : fallback;
}

function nullableString(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function nullableNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function proposalDominoPrefix(proposal: Pick<MachineProposal, 'domino' | 'action_type'>): string {
  const match = proposal.domino.match(/^\s*(D\d+\b[^:/|-]*)/i) ?? proposal.action_type.match(/^\s*(D\d+\b[^:/|-]*)/i);
  return (match?.[1] ?? proposal.domino).trim();
}

function proposalDominoMatches(proposal: Pick<MachineProposal, 'domino' | 'action_type'>, filter: string): boolean {
  const values = [proposalDominoPrefix(proposal), proposal.domino, proposal.action_type].map((value) => value.trim().toLowerCase()).filter(Boolean);
  return values.some((value) => value === filter || value.startsWith(filter) || filter.startsWith(value));
}

function compareValues(left: unknown, right: unknown, dir: SortDirection): number {
  const leftMissing = isMissingSortValue(left);
  const rightMissing = isMissingSortValue(right);
  if (leftMissing || rightMissing) {
    if (leftMissing && rightMissing) {
      return 0;
    }
    return leftMissing ? 1 : -1;
  }

  const leftNumber = sortNumber(left);
  const rightNumber = sortNumber(right);
  const base =
    leftNumber !== null && rightNumber !== null
      ? leftNumber - rightNumber
      : String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
  return dir === 'asc' ? base : -base;
}

function isMissingSortValue(value: unknown): boolean {
  return value === null || value === undefined || (typeof value === 'string' && (value.trim() === '' || value.trim().toLowerCase() === 'n/a'));
}

function sortNumber(value: unknown): number | null {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim().replace(/,/g, '').replace(/%$/, '');
  if (normalized === '') {
    return null;
  }
  const numeric = Number(normalized);
  return Number.isFinite(numeric) ? numeric : null;
}

function dominoIdFromLabel(label: string): string {
  return label.match(/^\s*(D\d+)\b/i)?.[1] ?? '';
}

function normalizeDominoId(id: string): string {
  const match = id.trim().match(/^D0*(\d+)$/i);
  return match ? `D${Number(match[1])}` : id.trim();
}

function dominoSortValue(id: string): number {
  const match = id.match(/^D0*(\d+)$/i);
  return match ? Number(match[1]) : Number.MAX_SAFE_INTEGER;
}

// --- C10 checks (C10-6) -----------------------------------------------------
// The weekly check/action system's cockpit surface. Two payloads, mirroring the two
// narrow API sections: the registered checks with their resolved config, and a
// server-paginated page of live findings.

export type MachineCheck = {
  check_key: string;
  title: string;
  description: string;
  enabled: boolean;
  mode: string;
  severity: string;
  auto_eligible: boolean;
  configured: boolean;
  auto_refused: boolean;
};

export type MachineCheckRun = {
  run_id: string;
  status: string;
  started_at: string | null;
  finished_at: string | null;
  checks_run: number | null;
  findings_open: number | null;
  checks_errored: number | null;
  digest_sent_at: string | null;
  digest_error: string | null;
  dry_run: boolean;
};

export type MachineChecksSection = {
  checks: MachineCheck[];
  config_error: string | null;
  last_run: MachineCheckRun | null;
};

export type MachineCheckFinding = {
  finding_id: string;
  check_key: string;
  subject_ref: string;
  market: string;
  state: string;
  severity: string;
  detail: Record<string, unknown>;
  money_currency: string | null;
  money_native: number | null;
  money_gbp: number | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
};

export type MachineCheckFindingsPage = {
  findings: MachineCheckFinding[];
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

export function parseChecksSection(value: unknown): MachineChecksSection {
  if (!isRecord(value)) return { checks: [], config_error: null, last_run: null };
  const rawChecks = Array.isArray(value.checks) ? value.checks : [];
  const checks = rawChecks.filter(isRecord).flatMap((row): MachineCheck[] => {
    const key = nullableString(row.check_key);
    if (!key) return [];
    return [{
      check_key: key,
      title: stringValue(row.title, key),
      description: stringValue(row.description, ''),
      enabled: row.enabled !== false,
      mode: stringValue(row.mode, 'signoff'),
      severity: stringValue(row.severity, 'warn'),
      auto_eligible: row.auto_eligible === true,
      configured: row.configured === true,
      auto_refused: row.auto_refused === true,
    }];
  });
  const run = isRecord(value.last_run) ? value.last_run : null;
  return {
    checks,
    config_error: nullableString(value.config_error),
    last_run: run && nullableString(run.run_id)
      ? {
          run_id: String(run.run_id),
          status: stringValue(run.status, 'unknown'),
          started_at: nullableString(run.started_at),
          finished_at: nullableString(run.finished_at),
          checks_run: nullableNumber(run.checks_run),
          findings_open: nullableNumber(run.findings_open),
          checks_errored: nullableNumber(run.checks_errored),
          digest_sent_at: nullableString(run.digest_sent_at),
          digest_error: nullableString(run.digest_error),
          dry_run: run.dry_run === true,
        }
      : null,
  };
}

export function parseCheckFindingsPage(value: unknown): MachineCheckFindingsPage {
  const empty = { findings: [], total: 0, limit: 100, offset: 0, has_more: false };
  if (!isRecord(value)) return empty;
  const rows = Array.isArray(value.findings) ? value.findings : [];
  const findings = rows.filter(isRecord).flatMap((row): MachineCheckFinding[] => {
    const findingId = nullableString(row.finding_id);
    const checkKey = nullableString(row.check_key);
    if (!findingId || !checkKey) return [];
    return [{
      finding_id: findingId,
      check_key: checkKey,
      subject_ref: stringValue(row.subject_ref, ''),
      market: stringValue(row.market, 'unknown'),
      state: stringValue(row.state, 'open'),
      severity: stringValue(row.severity, 'warn'),
      detail: isRecord(row.detail) ? row.detail : {},
      money_currency: nullableString(row.money_currency),
      money_native: nullableNumber(row.money_native),
      money_gbp: nullableNumber(row.money_gbp),
      first_seen_at: nullableString(row.first_seen_at),
      last_seen_at: nullableString(row.last_seen_at),
    }];
  });
  return {
    findings,
    total: nullableNumber(value.total) ?? findings.length,
    limit: nullableNumber(value.limit) ?? 100,
    offset: nullableNumber(value.offset) ?? 0,
    has_more: value.has_more === true,
  };
}
